import { useQuery } from "convex/react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/convex";

const avatarColors = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-purple-100 text-purple-700",
  "bg-orange-100 text-orange-700",
  "bg-indigo-100 text-indigo-700",
  "bg-pink-100 text-pink-700",
  "bg-red-100 text-red-700",
  "bg-violet-100 text-violet-700",
];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) + hash * 31 + name.charCodeAt(i)) | 0;
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

const Members = () => {
  const members = useQuery(api.members.list, {});

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-6 py-10 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-semibold text-foreground mb-2 tracking-tight">
            Member Directory
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base tracking-wide">
            Energy freedom advocates and industry leaders
          </p>
        </div>

        {/* Confidential Notice */}
        <Card className="mb-8 bg-amber-50/50 border-amber-200/50">
          <CardContent className="py-5 px-5">
            <h3 className="font-semibold text-foreground mb-2">Confidential Member Directory</h3>
            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
              This list of Energy Talking Point Executive Network members is confidential and for members only. If you'd like to connect with a member,
              please reach out to{" "}
              <a href="mailto:michelle@alexepstein.com" className="text-primary hover:underline">
                michelle@alexepstein.com
              </a>{" "}
              with your introduction request. We're happy to facilitate connections between members with
              shared interests or complementary expertise.
            </p>
            <p className="text-sm text-muted-foreground">
              For questions or to take your name off this list, please contact{" "}
              <a href="mailto:michelle@alexepstein.com" className="text-primary hover:underline">
                michelle@alexepstein.com
              </a>.
            </p>
          </CardContent>
        </Card>

        {/* Loading State */}
        {members === undefined && (
          <div className="divide-y divide-border/60">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 py-4">
                <Skeleton className="w-12 h-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Member List */}
        {members && (
          <div className="divide-y divide-border/60">
            {members.map((member) => (
              <div key={member._id} className="flex items-center gap-4 py-4">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className={`text-sm font-medium ${getAvatarColor(member.name)}`}>
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground">{member.name}</h3>
                  <p className="text-sm text-muted-foreground truncate">
                    <span className="font-medium text-foreground/80">{member.company}</span>
                    {member.title && (
                      <>
                        <span className="mx-2">|</span>
                        <span>{member.title}</span>
                      </>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Members;
